/**
 * useCrypto Helpers
 *
 * Pure utility functions for device key (de)serialization and browser detection.
 * Extracted from useCrypto.ts for maintainability.
 * No circular imports — does NOT import from useCrypto.ts.
 */

import { nanoid } from "../lib/nanoid";
import { toBase64, fromBase64, FALLBACK_PREKEY_ID, type DeviceKeys } from "@kollegeplus/crypto";
import type { StoredDeviceKeys } from "../lib/crypto-storage";

// ─── Constants ──────────────────────────────────────────────────────────────

export const DEVICE_ID_STORAGE_KEY = "kollegeplus:deviceId";

// ─── Device ID ──────────────────────────────────────────────────────────────

/** Read a cookie by name */
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Set a long-lived cookie (400 days, Secure, SameSite=Strict) */
function setCookie(name: string, value: string): void {
  const maxAge = 400 * 24 * 60 * 60; // 400 days (max allowed by browsers)
  const secure = location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Strict${secure}`;
}

export function generateDeviceId(): string {
  // Try localStorage first, then cookie fallback
  const fromLS = localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (fromLS) {
    // Sync to cookie
    setCookie("kp_deviceId", fromLS);
    return fromLS;
  }

  const fromCookie = getCookie("kp_deviceId");
  if (fromCookie) {
    // Restore to localStorage
    localStorage.setItem(DEVICE_ID_STORAGE_KEY, fromCookie);
    return fromCookie;
  }

  const id = `web-${nanoid(16)}`;
  localStorage.setItem(DEVICE_ID_STORAGE_KEY, id);
  setCookie("kp_deviceId", id);
  return id;
}

/** Get the persistent device ID (without generating a new one) */
export function getPersistedDeviceId(): string | null {
  return localStorage.getItem(DEVICE_ID_STORAGE_KEY) ?? getCookie("kp_deviceId");
}

/**
 * Drop the persisted device identity (localStorage key + the 400-day
 * `kp_deviceId` cookie) so the NEXT `generateDeviceId()` mints a brand-new id
 * instead of resurrecting the old one.
 *
 * CR1 cascade auto-recovery needs this: clearing the key stores alone is not
 * enough — `generateDeviceId()` reads this LS key / cookie first and would
 * otherwise re-register the cascade-dead deviceId with fresh keys (old id +
 * new keys → the bot's stale session for that id keeps failing). A genuinely
 * fresh id is what lets peers/bots establish a clean session.
 */
export function clearPersistedDeviceId(): void {
  try { localStorage.removeItem(DEVICE_ID_STORAGE_KEY); } catch { /* private mode — non-critical */ }
  try {
    // max-age=0 expires the cookie immediately (mirror setCookie's attributes).
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `kp_deviceId=; path=/; max-age=0; SameSite=Strict${secure}`;
  } catch { /* no document (non-browser) — non-critical */ }
}

// ─── Browser Detection ──────────────────────────────────────────────────────

export function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  if (ua.includes("Edge")) return "Edge";
  return "Web Browser";
}

// ─── Key Serialization ──────────────────────────────────────────────────────

export function serializeDeviceKeys(deviceId: string, keys: DeviceKeys): StoredDeviceKeys {
  return {
    deviceId,
    identityPrivateKey: toBase64(keys.identityKeyPair.curve25519.privateKey),
    identityPublicKey: toBase64(keys.identityKeyPair.curve25519.publicKey),
    signingPrivateKey: toBase64(keys.identityKeyPair.ed25519.privateKey),
    signingPublicKey: toBase64(keys.identityKeyPair.ed25519.publicKey),
    signedPreKey: {
      keyId: keys.signedPreKey.keyId,
      privateKey: toBase64(keys.signedPreKey.keyPair.privateKey),
      publicKey: toBase64(keys.signedPreKey.keyPair.publicKey),
      signature: toBase64(keys.signedPreKey.signature),
    },
    ...(keys.previousSignedPreKeys && keys.previousSignedPreKeys.length > 0
      ? {
          previousSignedPreKeys: keys.previousSignedPreKeys.map((s) => ({
            keyId: s.keyId,
            privateKey: toBase64(s.keyPair.privateKey),
            publicKey: toBase64(s.keyPair.publicKey),
            signature: toBase64(s.signature),
          })),
        }
      : {}),
    preKeys: keys.preKeys.map((pk) => ({
      keyId: pk.keyId,
      privateKey: toBase64(pk.keyPair.privateKey),
      publicKey: toBase64(pk.keyPair.publicKey),
    })),
    ...(keys.fallbackPreKey
      ? {
          fallbackPreKey: {
            keyId: keys.fallbackPreKey.keyId,
            privateKey: toBase64(keys.fallbackPreKey.keyPair.privateKey),
            publicKey: toBase64(keys.fallbackPreKey.keyPair.publicKey),
          },
        }
      : {}),
    createdAt: new Date().toISOString(),
  };
}

export function deserializeDeviceKeys(stored: StoredDeviceKeys): DeviceKeys {
  return {
    identityKeyPair: {
      curve25519: {
        privateKey: fromBase64(stored.identityPrivateKey),
        publicKey: fromBase64(stored.identityPublicKey),
      },
      ed25519: {
        privateKey: fromBase64(stored.signingPrivateKey),
        publicKey: fromBase64(stored.signingPublicKey),
      },
    },
    signedPreKey: {
      keyId: stored.signedPreKey.keyId,
      keyPair: {
        privateKey: fromBase64(stored.signedPreKey.privateKey),
        publicKey: fromBase64(stored.signedPreKey.publicKey),
      },
      signature: fromBase64(stored.signedPreKey.signature),
    },
    ...(stored.previousSignedPreKeys && stored.previousSignedPreKeys.length > 0
      ? {
          previousSignedPreKeys: stored.previousSignedPreKeys.map((s) => ({
            keyId: s.keyId,
            keyPair: {
              privateKey: fromBase64(s.privateKey),
              publicKey: fromBase64(s.publicKey),
            },
            signature: fromBase64(s.signature),
          })),
        }
      : {}),
    preKeys: stored.preKeys.map((pk) => ({
      keyId: pk.keyId,
      keyPair: {
        privateKey: fromBase64(pk.privateKey),
        publicKey: fromBase64(pk.publicKey),
      },
    })),
    ...(stored.fallbackPreKey
      ? {
          fallbackPreKey: {
            keyId: stored.fallbackPreKey.keyId,
            keyPair: {
              privateKey: fromBase64(stored.fallbackPreKey.privateKey),
              publicKey: fromBase64(stored.fallbackPreKey.publicKey),
            },
          },
        }
      : {}),
  };
}

export function findPreKey(
  deviceKeys: DeviceKeys,
  keyId: number | undefined,
): { privateKey: Uint8Array; publicKey: Uint8Array } | null {
  if (keyId === undefined) return null;
  const pk = deviceKeys.preKeys.find((k) => k.keyId === keyId);
  if (pk) return pk.keyPair;
  // Fix 3: a sender that ran into our empty OPK pool used the last-resort
  // fallback pre-key (served by the server in the OPK slot). Resolve it here so
  // X3DH receive succeeds; it is intentionally NOT in the consumable preKeys array.
  if (deviceKeys.fallbackPreKey && deviceKeys.fallbackPreKey.keyId === keyId) {
    return deviceKeys.fallbackPreKey.keyPair;
  }
  return null;
}

/**
 * Resolve the OPK private for an incoming X3DH initial. In-memory deviceKeys can
 * lag IDB after a cross-tab OPK replenish, so an in-memory miss is NOT proof the
 * OPK is gone. On a miss, re-read IDB once (via the injected loader) and retry
 * before the caller concludes "stale bundle" + drops the message. `recovered=true`
 * ⟹ the in-memory snapshot was stale; the caller should heal it + count the
 * recovery. Found / SPK-only (no OPK) / the reusable fallback key never recover.
 */
export async function resolvePreKeyWithRefresh(
  inMemory: DeviceKeys,
  usedPreKeyId: number | undefined,
  loadFresh: () => Promise<DeviceKeys | null>,
): Promise<{ preKey: ReturnType<typeof findPreKey>; deviceKeys: DeviceKeys; recovered: boolean }> {
  const preKey = findPreKey(inMemory, usedPreKeyId);
  if (preKey || usedPreKeyId === undefined || usedPreKeyId === FALLBACK_PREKEY_ID) {
    return { preKey, deviceKeys: inMemory, recovered: false };
  }
  const fresh = await loadFresh();
  if (fresh) {
    const retry = findPreKey(fresh, usedPreKeyId);
    if (retry) return { preKey: retry, deviceKeys: fresh, recovered: true };
  }
  return { preKey: null, deviceKeys: inMemory, recovered: false };
}
